import { useEffect } from 'react';
import { getTextWebSocketManager } from '../Apis/AIPrescriptionApis/websocketManager';
import { extractPharmacyFromMessage } from '../utils/AIPrescription/pharmacyExtractionUtils';
import { extractAIResponse } from '../utils/AIPrescription/responseUtils';
import { safeUpdateConversation } from '../utils/AIPrescription/conversationUpdateHelpers';
import { buildEnhancedDrugInteractionResponse } from '../utils/AIPrescription/drugInteractionResponse';
import { WS_MESSAGE_TYPES, INTENT_TYPES, PHARMACY_FIELDS } from '../utils/AIPrescription/constants';
import { recordServerFieldCorrections } from '../utils/AIPrescription/prescriptionEditsStore';
import { applySessionBenefitSourceFromResult } from '../utils/AIPrescription/sessionBenefitSourceStore';
import { getPatientId } from '../utils/patientSession';
import { 
  createEmptyMedicationData, 
  isValidMedicationItem, 
  createPrescriptionsFromMedicationData,
  ConversationItem,
  findTargetConversation,
  updateConversationById as updateConversationByIdUtil,
  getPreviousMedicationData,
  multiIntentFromMessageData,
  medicationSuggestionGroupsFromPayload,
  pharmacySuggestionGroupsFromPayload,
  flatLabelsFromSuggestionGroups,
} from '../utils/AIPrescription';
import { normalizeAppTabLinks } from '../utils/AIPrescription/appTabLink';

type ConvDrugResponse = NonNullable<ConversationItem['drugInteractionResponse']>;

/** Merge `message.data.ui_blocks` when the server includes it; otherwise keep `previous`. */
function uiBlocksFromResultData(data: unknown, previous?: string[]): string[] | undefined {
  if (!data || typeof data !== "object") return previous;
  const d = data as Record<string, unknown>;
  if (!("ui_blocks" in d)) return previous;
  const raw = d.ui_blocks;
  if (!Array.isArray(raw)) return previous;
  return raw.map((x) => String(x).trim()).filter((s) => s.length > 0);
}

/** Structural tab-nav payload(s) from orchestration; allowlisted paths only. */
function appTabLinksFromResultData(
  data: unknown,
  previous?: ConversationItem["appTabLinks"]
): ConversationItem["appTabLinks"] | undefined {
  if (!data || typeof data !== "object") return previous;
  const d = data as Record<string, unknown>;
  if (!("app_tab_links" in d) && !("app_tab_link" in d)) return previous;
  const normalized = normalizeAppTabLinks(
    d.app_tab_links !== undefined ? d.app_tab_links : d.app_tab_link
  );
  return normalized.length > 0 ? normalized : undefined;
}

function appTabLinkFromResultData(
  data: unknown,
  previous?: ConversationItem["appTabLink"]
): ConversationItem["appTabLink"] | undefined {
  const links = appTabLinksFromResultData(data, previous ? [previous] : undefined);
  return links?.[0];
}

/** Read `data.pharmacy_search_fallback_mode` (structural flag, never derived from text). */
function pharmacySearchFallbackModeFromResultData(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const raw = (data as Record<string, unknown>).pharmacy_search_fallback_mode;
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
}

/**
 * Build pharmacy rows for ``pharmacyLookup``: prefer ``pharmacy_suggestions_data``, then
 * ``pharmacy_suggestions``, then nested ``medication_data[].pharmacy_suggestions_data``.
 */
function pharmacyLookupRowsFromResultData(data: unknown): unknown[] {
  if (!data || typeof data !== "object") return [];
  const d = data as Record<string, unknown>;
  let pharmacies: unknown = d.pharmacy_suggestions_data;
  if (!Array.isArray(pharmacies) || pharmacies.length === 0) {
    pharmacies = d[PHARMACY_FIELDS.PHARMACY_SUGGESTIONS];
  }
  if (!Array.isArray(pharmacies) || pharmacies.length === 0) {
    const meds = d.medication_data;
    if (Array.isArray(meds)) {
      for (const m of meds) {
        if (m && typeof m === "object") {
          const inner = (m as Record<string, unknown>).pharmacy_suggestions_data;
          if (Array.isArray(inner) && inner.length > 0) {
            pharmacies = inner;
            break;
          }
        }
      }
    }
  }
  if (pharmacies && typeof pharmacies === "object" && !Array.isArray(pharmacies)) {
    const o = pharmacies as Record<string, unknown>;
    pharmacies = o.items || o.data || o.list || [];
  }
  if (!Array.isArray(pharmacies)) {
    return pharmacies ? [pharmacies] : [];
  }
  if (pharmacies.length === 0 && d.pharmacy) {
    return [d.pharmacy];
  }
  return pharmacies;
}

/**
 * Build PBM/benefit-source rows for ``pbmLookup`` from ``pbm_suggestions_data``.
 */
function pbmLookupRowsFromResultData(data: unknown): unknown[] {
  if (!data || typeof data !== "object") return [];
  const d = data as Record<string, unknown>;
  const rows = d.pbm_suggestions_data;
  return Array.isArray(rows) ? rows : [];
}

/**
 * Build favourite (saved) drug rows for ``favouriteDrugs`` from ``favourite_drugs_data``.
 */
function favouriteDrugsRowsFromResultData(data: unknown): unknown[] {
  if (!data || typeof data !== "object") return [];
  const d = data as Record<string, unknown>;
  const rows = d.favourite_drugs_data;
  return Array.isArray(rows) ? rows : [];
}

function mergeDrugInteractionFromMessage(
  message: any,
  prevDrug: ConvDrugResponse | undefined
): ConvDrugResponse | undefined {
  const fromMessage = buildEnhancedDrugInteractionResponse(message);
  if (!fromMessage) return undefined;
  return {
    ...fromMessage,
    intent: (fromMessage.intent || prevDrug?.intent || "").toString().toLowerCase(),
    high_severity_descriptions:
      (fromMessage.high_severity_descriptions?.length ? fromMessage.high_severity_descriptions : undefined) ??
      prevDrug?.high_severity_descriptions,
    moderate_severity_descriptions:
      (fromMessage.moderate_severity_descriptions?.length ? fromMessage.moderate_severity_descriptions : undefined) ??
      prevDrug?.moderate_severity_descriptions,
    minor_severity_descriptions:
      (fromMessage.minor_severity_descriptions?.length
        ? fromMessage.minor_severity_descriptions
        : undefined) ?? prevDrug?.minor_severity_descriptions,
    current_medication_only_counts:
      fromMessage.current_medication_only_counts ?? prevDrug?.current_medication_only_counts,
    high_severity_rtf_primary_medication_name:
      (fromMessage.high_severity_rtf_primary_medication_name?.trim()
        ? fromMessage.high_severity_rtf_primary_medication_name.trim()
        : undefined) ?? prevDrug?.high_severity_rtf_primary_medication_name,
    high_severity_prescription_review_resolved:
      typeof message?.data?.high_severity_prescription_review_resolved === "boolean"
        ? message.data.high_severity_prescription_review_resolved
        : fromMessage.high_severity_prescription_review_resolved ?? prevDrug?.high_severity_prescription_review_resolved,
  } as ConvDrugResponse;
}

interface UseWebSocketMessageHandlerProps {
  isMountedRef: React.MutableRefObject<boolean>;
  resultMessageProcessedRef: React.MutableRefObject<boolean>;
  setConversations: React.Dispatch<React.SetStateAction<ConversationItem[]>>;
  setIsSearching: (value: boolean) => void;
  preferredPharmacy: string;
  updatePharmacy: (data: any) => void;
}

export const useWebSocketMessageHandler = ({
  isMountedRef,
  resultMessageProcessedRef,
  setConversations,
  setIsSearching,
  preferredPharmacy,
  updatePharmacy,
}: UseWebSocketMessageHandlerProps) => {
  useEffect(() => {
    isMountedRef.current = true;
    const wsManager = getTextWebSocketManager();

    // Pre-warm the socket on mount so the FIRST message doesn't pay the full cost (auth-token
    // fetch + WS handshake + backend MCP cold-connect) under the send's tight timeout. By the
    // time the doctor reads/types, the connection is already OPEN. connect() is idempotent
    // (no-ops if already CONNECTING/OPEN), so this is safe to call here.
    try {
      wsManager.ensureConnected();
    } catch (e) {
      // Non-fatal: the lazy connect in sendRequest still covers it.
    }

    wsManager.setPreferredPharmacyCallback((data: any) => {
      if (!isMountedRef.current) return;
      updatePharmacy(data);
    });

    const handleResultMessage = (message: any) => {
      applySessionBenefitSourceFromResult(getPatientId(), message?.data);

      // Extract pharmacy if present
      const pharmacyData = extractPharmacyFromMessage(message);
      if (pharmacyData) {
        updatePharmacy(pharmacyData);
      }

      // Attach the Langfuse trace id (sent by the server on each result) to the
      // active turn so a later thumbs up/down can be persisted against THIS turn.
      const _traceId =
        (message?.data && (message.data.traceId || message.data.trace_id)) || undefined;
      if (_traceId) {
        setConversations((prev) => {
          if (!isMountedRef.current) return prev;
          const target = findTargetConversation(prev);
          if (!target) return prev;
          return prev.map((c) =>
            c.id === target.id ? { ...c, traceId: _traceId } : c
          );
        });
      }

      // Structural "which field value did the server REJECT this turn" signal
      // (`field_corrections` — an invalid update auto-resolved to the only valid catalog
      // candidate). Recorded here, alongside the sent-medication stamp below and for the
      // same reason: it arrives independently of which intent branch handles the rest of
      // this message. Retires the clinician's now-dead inline card edit, which no amount of
      // comparing server values could detect — a rejected edit leaves the field unchanged.
      recordServerFieldCorrections(
        getPatientId(),
        message?.data?.field_corrections,
        message?.data?.medication_data
      );

      // Structural "which medication(s) did THIS turn's send actually transmit" signal
      // (`sent_medication_data`, present on every server result whose `intent` is
      // "complete" — full or partial send). Independent of which branch below handles
      // the rest of this message, mirroring the `_traceId` stamp above, so
      // `applyRetroactiveSentStatus` (conversationUtils.ts) can back-fill `sent: true`
      // onto exactly the right earlier card(s) instead of text-sniffing the response and
      // marking every medication that turn ever displayed as sent.
      const _sentMedicationNames = Array.isArray(message?.data?.sent_medication_data)
        ? Array.from(
            new Set(
              (message.data.sent_medication_data as any[])
                .map((m) => (m && typeof m === 'object' ? String(m.medication_name || m.drugName || '').trim() : ''))
                .filter((name): name is string => name.length > 0)
            )
          )
        : undefined;
      if (_sentMedicationNames && _sentMedicationNames.length > 0) {
        setConversations((prev) => {
          if (!isMountedRef.current) return prev;
          const target = findTargetConversation(prev);
          if (!target) return prev;
          return prev.map((c) =>
            c.id === target.id ? { ...c, sentMedicationNames: _sentMedicationNames } : c
          );
        });
      }

      const mi = multiIntentFromMessageData(message.data);
      const branchIntent = mi.branchIntent;
      const intentEnvelope = {
        lastIntent: branchIntent || (typeof message.data?.intent === 'string' ? message.data.intent.toLowerCase() : ''),
        primaryIntent: mi.primaryIntent || undefined,
        intents: mi.intents.length > 0 ? mi.intents : undefined,
      };
   
      const mergeMedArray = message.data?.medication_data || [];
      const mergeHasMedData = Array.isArray(mergeMedArray) && mergeMedArray.length > 0;
      const mergeAiResponse = extractAIResponse(message.data);

      // Skip if already processed — unless this message adds drug interaction or pharmacy data (later RESULT)
      if (resultMessageProcessedRef.current) {
        // When the server sends multiple RESULT messages (e.g. first medications, then suggestions),
        // we must still merge the later suggestions into the same conversation.
        if (branchIntent === INTENT_TYPES.SUGGESTIONS) {
          const medicationDataArray = message.data?.medication_data || [];
          const hasMedicationData =
            Array.isArray(medicationDataArray) && medicationDataArray.length > 0;
          const suggestionGroups = medicationSuggestionGroupsFromPayload(
            message.data,
            hasMedicationData ? medicationDataArray : undefined
          );
          const flatFromGroups = flatLabelsFromSuggestionGroups(suggestionGroups);
          const suggestions =
            flatFromGroups.length > 0
              ? flatFromGroups
              : message.data?.suggestions || [];

          const aiResponse = extractAIResponse(message.data);
          const mergePharmacies = pharmacyLookupRowsFromResultData(message.data);
          const pharmacyGroups = pharmacySuggestionGroupsFromPayload(
            message.data,
            hasMedicationData ? medicationDataArray : undefined
          );

          setConversations((prev) => {
            if (!isMountedRef.current) return prev;
            const targetConversation = findTargetConversation(prev);
            if (!targetConversation) return prev;

            const suggestionUpdates: Record<string, unknown> = {
              aiResponse: aiResponse || targetConversation.aiResponse || "",
              isLoading:
                !(aiResponse || targetConversation.aiResponse) ||
                (aiResponse || targetConversation.aiResponse || "").trim() === "",
              suggestions,
              suggestionGroups:
                suggestionGroups.length > 0 ? suggestionGroups : undefined,
              structuredMedicationData: hasMedicationData
                ? [...medicationDataArray]
                : targetConversation.structuredMedicationData || [],
              ...intentEnvelope,
              currentState: message.data?.current_state ?? targetConversation.currentState,
              uiBlocks: uiBlocksFromResultData(message.data, targetConversation.uiBlocks),
              appTabLink: appTabLinkFromResultData(message.data, targetConversation.appTabLink),
              appTabLinks: appTabLinksFromResultData(message.data, targetConversation.appTabLinks),
            };
            if (pharmacyGroups.length > 0) {
              suggestionUpdates.pharmacySuggestionGroups = pharmacyGroups;
            }
            if (mergePharmacies.length > 0) {
              suggestionUpdates.pharmacyLookup = mergePharmacies;
              suggestionUpdates.pharmacySearchFallbackMode =
                pharmacySearchFallbackModeFromResultData(message.data);
            }
            const mergedDi = mergeDrugInteractionFromMessage(
              message,
              targetConversation.drugInteractionResponse
            );
            if (mergedDi) suggestionUpdates.drugInteractionResponse = mergedDi;

            return updateConversationByIdUtil(
              prev,
              targetConversation.id,
              suggestionUpdates,
              "✅ WebSocket: Merge suggestions from later RESULT"
            );
          });

          if (isMountedRef.current) setIsSearching(false);
          return;
        }

        const isPharmacyIntent = branchIntent === INTENT_TYPES.PHARMACY_LOOKUP || branchIntent === INTENT_TYPES.GET_FAVOURITE_PHARMACY || branchIntent === INTENT_TYPES.PHARMACY_SUGGESTIONS;
        const pharmacies = isPharmacyIntent ? pharmacyLookupRowsFromResultData(message.data) : [];
        const hasPharmacyData = isPharmacyIntent && pharmacies.length > 0;
        const pharmacySuggestionGroups = medicationSuggestionGroupsFromPayload(
          message.data,
          mergeHasMedData ? mergeMedArray : undefined
        );
        const pharmacyGroups = pharmacySuggestionGroupsFromPayload(
          message.data,
          mergeHasMedData ? mergeMedArray : undefined
        );
        const pharmacyFlatSuggestions =
          flatLabelsFromSuggestionGroups(pharmacySuggestionGroups);
        const medSuggestions = message.data?.suggestions;
        const hasMedicationSuggestionData =
          pharmacyFlatSuggestions.length > 0 ||
          (Array.isArray(medSuggestions) && medSuggestions.length > 0);

        if (hasPharmacyData || (isPharmacyIntent && hasMedicationSuggestionData)) {
          setConversations((prev) => {
            if (!isMountedRef.current) return prev;
            const targetConversation = findTargetConversation(prev);
            if (!targetConversation) return prev;
            const pharmacyUpdates: Record<string, unknown> = {
              ...intentEnvelope,
              currentState: message.data?.current_state ?? targetConversation.currentState,
              uiBlocks: uiBlocksFromResultData(message.data, targetConversation.uiBlocks),
              appTabLink: appTabLinkFromResultData(message.data, targetConversation.appTabLink),
              appTabLinks: appTabLinksFromResultData(message.data, targetConversation.appTabLinks),
            };
            if (hasPharmacyData) {
              pharmacyUpdates.pharmacyLookup = pharmacies;
              pharmacyUpdates.pharmacySearchFallbackMode =
                pharmacySearchFallbackModeFromResultData(message.data);
            }
            if (pharmacyGroups.length > 0) {
              pharmacyUpdates.pharmacySuggestionGroups = pharmacyGroups;
            }
            if (pharmacyFlatSuggestions.length > 0) {
              pharmacyUpdates.suggestions = pharmacyFlatSuggestions;
              pharmacyUpdates.suggestionGroups = pharmacySuggestionGroups;
            } else if (Array.isArray(medSuggestions) && medSuggestions.length > 0) {
              pharmacyUpdates.suggestions = medSuggestions;
            }
            if (mergeHasMedData) {
              const prescriptions = createPrescriptionsFromMedicationData(
                mergeMedArray,
                {
                  aiResponse: mergeAiResponse || targetConversation.aiResponse || "",
                  defaultPharmacy: preferredPharmacy,
                  existingPrescriptions: targetConversation.prescriptions || [],
                }
              );
              pharmacyUpdates.structuredMedicationData = [...mergeMedArray];
              pharmacyUpdates.prescriptions = [...prescriptions];
              pharmacyUpdates.originalPrescription = prescriptions[0];
            }
            const mergedPharmacyDi = mergeDrugInteractionFromMessage(
              message,
              targetConversation.drugInteractionResponse
            );
            if (mergedPharmacyDi) pharmacyUpdates.drugInteractionResponse = mergedPharmacyDi;
            return updateConversationByIdUtil(
              prev,
              targetConversation.id,
              pharmacyUpdates,
              "✅ WebSocket: Merge pharmacy suggestions from later RESULT"
            );
          });
          if (isMountedRef.current) setIsSearching(false);
          return;
        }

        const hasDrugInteractionData =
          (Array.isArray(message.data?.drug_interactions) && message.data.drug_interactions.length > 0) ||
          (Array.isArray(message.data?.drug_interaction_response_raw?.drug_interactions) && message.data.drug_interaction_response_raw.drug_interactions.length > 0) ||
          (message.data?.drug_interaction_response != null);
        if (hasDrugInteractionData) {
          setConversations((prev) =>
            safeUpdateConversation(
              prev,
              (conv) => {
                const merged = mergeDrugInteractionFromMessage(message, conv.drugInteractionResponse);
                const medArray = message.data?.medication_data;
                const validMedicationData = Array.isArray(medArray)
                  ? medArray.filter(isValidMedicationItem)
                  : [];
                const hasMeds = validMedicationData.length > 0;

                if (!merged && !hasMeds) return conv;

                const aiFromMessage = extractAIResponse(message.data);
                let next: ConversationItem = { ...conv };

                if (merged) {
                  next = { ...next, drugInteractionResponse: merged };
                }

                // Second RESULT often carries fresh medication_data (e.g. refills) only with DI;
                // without this, prescriptions stay stale while structuredMedicationData would never update.
                if (hasMeds) {
                  const substitutionsToUse = conv.pendingSubstitutions;
                  const prescriptionOptions: {
                    aiResponse: string;
                    defaultPharmacy: string;
                    existingPrescriptions: any[];
                    substitutions?: string;
                  } = {
                    aiResponse: aiFromMessage || conv.aiResponse || "",
                    defaultPharmacy: preferredPharmacy,
                    existingPrescriptions: conv.prescriptions || [],
                  };
                  if (substitutionsToUse !== undefined) {
                    prescriptionOptions.substitutions = substitutionsToUse;
                  }
                  const prescriptions = createPrescriptionsFromMedicationData(
                    validMedicationData,
                    prescriptionOptions
                  );
                  const effectiveResponse = aiFromMessage || conv.aiResponse || "";
                  next = {
                    ...next,
                    prescriptions: [...prescriptions],
                    originalPrescription: prescriptions[0],
                    structuredMedicationData: [...validMedicationData],
                    aiResponse: effectiveResponse,
                    isLoading: !effectiveResponse || effectiveResponse.trim() === "",
                    currentState: message.data?.current_state ?? conv.currentState,
                    lastIntent:
                      intentEnvelope.lastIntent ||
                      (typeof message.data?.intent === 'string' ? message.data.intent.toLowerCase() : '') ||
                      conv.lastIntent,
                    primaryIntent: intentEnvelope.primaryIntent ?? conv.primaryIntent,
                    intents: intentEnvelope.intents ?? conv.intents,
                    pendingSubstitutions: undefined,
                    uiBlocks: uiBlocksFromResultData(message.data, conv.uiBlocks),
                    appTabLink: appTabLinkFromResultData(message.data, conv.appTabLink),
                    appTabLinks: appTabLinksFromResultData(message.data, conv.appTabLinks),
                  };
                }

                return {
                  ...next,
                  uiBlocks: uiBlocksFromResultData(message.data, conv.uiBlocks) ?? next.uiBlocks,
                  appTabLink: appTabLinkFromResultData(message.data, conv.appTabLink) ?? next.appTabLink,
                  appTabLinks: appTabLinksFromResultData(message.data, conv.appTabLinks) ?? next.appTabLinks,
                };
              },
              () => isMountedRef.current,
              "✅ WebSocket: Merge drug interaction data from later RESULT"
            )
          );
        } else {
          // Generic fallback: a later RESULT chunk whose intent isn't one of the specific
          // shapes handled above (suggestions / pharmacy / drug-interaction) must still
          // update the visible response instead of being silently dropped. Without this, a
          // genuinely NEW turn's single result message (e.g. an "allergies" intent) could go
          // unrendered whenever resultMessageProcessedRef was left true from an earlier,
          // unrelated turn -- a real gap even though it was not the cause of the reported
          // allergy screenshot (that turn's aiResponse/allergyResponse DID update, proving it
          // took the primary, non-merge path). See CLAUDE.md's "no information" writeup.
          const fallbackAiResponse = extractAIResponse(message.data);
          const fallbackAllergyResponse =
            message?.data?.allergy_response && typeof message.data.allergy_response === 'object'
              ? (message.data.allergy_response as ConversationItem['allergyResponse'])
              : undefined;
          if (fallbackAiResponse || fallbackAllergyResponse) {
            setConversations((prev) =>
              safeUpdateConversation(
                prev,
                (conv) => ({
                  ...conv,
                  aiResponse: fallbackAiResponse || conv.aiResponse || "",
                  isLoading: false,
                  ...intentEnvelope,
                  lastIntent: intentEnvelope.lastIntent || conv.lastIntent,
                  allergyResponse: fallbackAllergyResponse ?? conv.allergyResponse,
                  currentState: message.data?.current_state ?? conv.currentState,
                  uiBlocks: uiBlocksFromResultData(message.data, conv.uiBlocks),
                  appTabLink: appTabLinkFromResultData(message.data, conv.appTabLink),
                  appTabLinks: appTabLinksFromResultData(message.data, conv.appTabLinks),
                }),
                () => isMountedRef.current,
                "✅ WebSocket: Merge generic follow-up RESULT (no specific shape matched)"
              )
            );
          }
        }
        if (isMountedRef.current) setIsSearching(false);
        return;
      }
      
      const intent = branchIntent;
      const aiResponse = extractAIResponse(message.data);
      const mfaPhaseFromMessage =
        typeof message.data?.mfa_phase === "string"
          ? message.data.mfa_phase.toLowerCase()
          : undefined;

      // Handle different intents
      if (intent === INTENT_TYPES.COMPLETE) {
        resultMessageProcessedRef.current = false;
        const medicationDataArray = Array.isArray(message.data?.medication_data)
          ? message.data.medication_data
          : [];
        const validMedicationData = medicationDataArray.filter(isValidMedicationItem);
        if (validMedicationData.length > 0 || message.data?.partial_send_complete === true) {
          setConversations((prev) =>
            safeUpdateConversation(
              prev,
              (conv) => {
                const mergedDi = mergeDrugInteractionFromMessage(message, conv.drugInteractionResponse);
                const prescriptionOptions: any = {
                  aiResponse,
                  defaultPharmacy: preferredPharmacy,
                  existingPrescriptions: conv.prescriptions || [],
                };
                const prescriptions = createPrescriptionsFromMedicationData(
                  validMedicationData,
                  prescriptionOptions
                );
                return {
                  ...conv,
                  aiResponse,
                  isLoading: !aiResponse || aiResponse.trim() === "",
                  prescriptions: [...prescriptions],
                  structuredMedicationData: [...validMedicationData],
                  originalPrescription: prescriptions[0],
                  currentState: message.data?.current_state ?? conv.currentState,
                  drugInteractionResponse: (
                    mergedDi ?? conv.drugInteractionResponse
                  ) as ConvDrugResponse | undefined,
                  ...intentEnvelope,
                  uiBlocks: uiBlocksFromResultData(message.data, conv.uiBlocks),
                  appTabLink: appTabLinkFromResultData(message.data, conv.appTabLink),
                  appTabLinks: appTabLinksFromResultData(message.data, conv.appTabLinks),
                };
              },
              () => isMountedRef.current,
              "✅ WebSocket: Partial send complete, preserving remaining prescriptions"
            )
          );
          setIsSearching(false);
          return;
        }
        const emptyMedicationData = createEmptyMedicationData();
        
        setConversations((prev) => 
          safeUpdateConversation(prev, {
            aiResponse,
            isLoading: !aiResponse || aiResponse.trim() === "",
            prescriptions: [],
            structuredMedicationData: emptyMedicationData,
            originalPrescription: undefined,
            ...intentEnvelope,
            uiBlocks: uiBlocksFromResultData(message.data, []),
            appTabLink: appTabLinkFromResultData(message.data, undefined),
            appTabLinks: appTabLinksFromResultData(message.data, undefined),
          }, () => isMountedRef.current, "✅ WebSocket: Hiding summary and resetting for new RX")
        );
        setIsSearching(false);
        return;
      }

      if (intent === INTENT_TYPES.SUGGESTIONS) {
        const medicationDataArray = message.data?.medication_data || [];
        const hasMedicationData = Array.isArray(medicationDataArray) && medicationDataArray.length > 0;
        const suggestionGroups = medicationSuggestionGroupsFromPayload(
          message.data,
          hasMedicationData ? medicationDataArray : undefined
        );
        const flatFromGroups = flatLabelsFromSuggestionGroups(suggestionGroups);
        const suggestions =
          flatFromGroups.length > 0
            ? flatFromGroups
            : message.data?.suggestions || [];

        const pharmacies = pharmacyLookupRowsFromResultData(message.data);
        const pharmacyGroups = pharmacySuggestionGroupsFromPayload(
          message.data,
          hasMedicationData ? medicationDataArray : undefined
        );

        setConversations((prev) => {
          if (!isMountedRef.current) return prev;
          const targetConversation = findTargetConversation(prev);
          if (!targetConversation) return prev;

          const updates: Record<string, any> = {
            aiResponse: aiResponse || targetConversation.aiResponse || "",
            isLoading: !(aiResponse || targetConversation.aiResponse) || (aiResponse || targetConversation.aiResponse || "").trim() === "",
            suggestions,
            suggestionGroups:
              suggestionGroups.length > 0 ? suggestionGroups : undefined,
            structuredMedicationData: hasMedicationData
              ? [...medicationDataArray]
              : (targetConversation.structuredMedicationData || []),
            ...intentEnvelope,
            uiBlocks: uiBlocksFromResultData(message.data, targetConversation.uiBlocks),
            appTabLink: appTabLinkFromResultData(message.data, targetConversation.appTabLink),
            appTabLinks: appTabLinksFromResultData(message.data, targetConversation.appTabLinks),
          };
          if (pharmacyGroups.length > 0) {
            updates.pharmacySuggestionGroups = pharmacyGroups;
          }
          if (pharmacies.length > 0) {
            updates.pharmacyLookup = pharmacies;
            updates.pharmacySearchFallbackMode =
              pharmacySearchFallbackModeFromResultData(message.data);
          }

          return updateConversationByIdUtil(prev, targetConversation.id, updates, "✅ WebSocket: Updated conversation with suggestions");
        });

        if (isMountedRef.current) setIsSearching(false);
        return;
      }

      if (intent === INTENT_TYPES.PBM_SUGGESTIONS) {
        const pbmRows = pbmLookupRowsFromResultData(message.data);
        setConversations((prev) => {
          if (!isMountedRef.current) return prev;
          const targetConversation = findTargetConversation(prev);
          if (!targetConversation) return prev;
          const pbmUpdates: Record<string, unknown> = {
            aiResponse: aiResponse || targetConversation.aiResponse || "",
            isLoading:
              !(aiResponse || targetConversation.aiResponse) ||
              (aiResponse || targetConversation.aiResponse || "").trim() === "",
            pbmLookup: pbmRows,
            ...intentEnvelope,
            currentState: message.data?.current_state ?? targetConversation.currentState,
            uiBlocks: uiBlocksFromResultData(message.data, targetConversation.uiBlocks),
            appTabLink: appTabLinkFromResultData(message.data, targetConversation.appTabLink),
            appTabLinks: appTabLinksFromResultData(message.data, targetConversation.appTabLinks),
          };
          return updateConversationByIdUtil(
            prev,
            targetConversation.id,
            pbmUpdates,
            "✅ WebSocket: Updated conversation with PBM benefit-source picker"
          );
        });
        if (isMountedRef.current) setIsSearching(false);
        return;
      }

      if (intent === INTENT_TYPES.FAVOURITE_DRUGS_SUGGESTIONS) {
        const favDrugRows = favouriteDrugsRowsFromResultData(message.data);
        setConversations((prev) => {
          if (!isMountedRef.current) return prev;
          const targetConversation = findTargetConversation(prev);
          if (!targetConversation) return prev;
          const favUpdates: Record<string, unknown> = {
            aiResponse: aiResponse || targetConversation.aiResponse || "",
            isLoading:
              !(aiResponse || targetConversation.aiResponse) ||
              (aiResponse || targetConversation.aiResponse || "").trim() === "",
            favouriteDrugs: favDrugRows,
            ...intentEnvelope,
            currentState: message.data?.current_state ?? targetConversation.currentState,
            uiBlocks: uiBlocksFromResultData(message.data, targetConversation.uiBlocks),
            appTabLink: appTabLinkFromResultData(message.data, targetConversation.appTabLink),
            appTabLinks: appTabLinksFromResultData(message.data, targetConversation.appTabLinks),
          };
          return updateConversationByIdUtil(
            prev,
            targetConversation.id,
            favUpdates,
            "✅ WebSocket: Updated conversation with favourite drugs picker"
          );
        });
        if (isMountedRef.current) setIsSearching(false);
        return;
      }

      if (intent === INTENT_TYPES.PHARMACY_LOOKUP || intent === INTENT_TYPES.GET_FAVOURITE_PHARMACY || intent === INTENT_TYPES.PHARMACY_SUGGESTIONS) {
        const pharmacies = pharmacyLookupRowsFromResultData(message.data);

        const medicationDataArray = message.data?.medication_data || [];
        const hasMedicationData = Array.isArray(medicationDataArray) && medicationDataArray.length > 0;
        const suggestionGroups = medicationSuggestionGroupsFromPayload(
          message.data,
          hasMedicationData ? medicationDataArray : undefined
        );
        const flatFromGroups = flatLabelsFromSuggestionGroups(suggestionGroups);
        const pharmacyGroups = pharmacySuggestionGroupsFromPayload(
          message.data,
          hasMedicationData ? medicationDataArray : undefined
        );

        setConversations((prev) => {
          if (!isMountedRef.current) return prev;
          const targetConversation = findTargetConversation(prev);
          if (!targetConversation) return prev;

          const topLevelSugg = message.data?.suggestions;
          const primaryPharmacyUpdates: Record<string, unknown> = {
            aiResponse: aiResponse || targetConversation.aiResponse || "",
            isLoading: !(aiResponse || targetConversation.aiResponse) || (aiResponse || targetConversation.aiResponse || "").trim() === "",
            pharmacyLookup: pharmacies,
            pharmacySearchFallbackMode: pharmacySearchFallbackModeFromResultData(message.data),
            structuredMedicationData: hasMedicationData
              ? [...medicationDataArray]
              : (targetConversation.structuredMedicationData || []),
            ...intentEnvelope,
            currentState: message.data?.current_state ?? targetConversation.currentState,
            uiBlocks: uiBlocksFromResultData(message.data, targetConversation.uiBlocks),
            appTabLink: appTabLinkFromResultData(message.data, targetConversation.appTabLink),
            appTabLinks: appTabLinksFromResultData(message.data, targetConversation.appTabLinks),
          };
          if (pharmacyGroups.length > 0) {
            primaryPharmacyUpdates.pharmacySuggestionGroups = pharmacyGroups;
          }
          if (flatFromGroups.length > 0) {
            primaryPharmacyUpdates.suggestions = flatFromGroups;
            primaryPharmacyUpdates.suggestionGroups = suggestionGroups;
          } else if (Array.isArray(topLevelSugg) && topLevelSugg.length > 0) {
            primaryPharmacyUpdates.suggestions = topLevelSugg;
          }
          if (hasMedicationData) {
            const prescriptions = createPrescriptionsFromMedicationData(
              medicationDataArray,
              {
                aiResponse: aiResponse || targetConversation.aiResponse || "",
                defaultPharmacy: preferredPharmacy,
                existingPrescriptions: targetConversation.prescriptions || [],
              }
            );
            primaryPharmacyUpdates.prescriptions = [...prescriptions];
            primaryPharmacyUpdates.originalPrescription = prescriptions[0];
          }
          const primaryPharmacyDi = mergeDrugInteractionFromMessage(
            message,
            targetConversation.drugInteractionResponse
          );
          if (primaryPharmacyDi) primaryPharmacyUpdates.drugInteractionResponse = primaryPharmacyDi;

          return updateConversationByIdUtil(
            prev,
            targetConversation.id,
            primaryPharmacyUpdates,
            "✅ WebSocket: Updated conversation with pharmacy lookup"
          );
        });

        if (isMountedRef.current) setIsSearching(false);
        return;
      }

      // Handle medication data processing
      resultMessageProcessedRef.current = true;
      const hasMedicationData = message.data.medication_data && 
                                Array.isArray(message.data.medication_data) && 
                                message.data.medication_data.length > 0;

      // Build enhanced drug interaction response from message (for view_interactions or when no medication data)
      const intentStr =
        branchIntent ||
        (typeof message.data?.intent === 'string' ? message.data.intent.toLowerCase() : '');
      // Use mergeDrugInteractionFromMessage in handlers so flags like high_severity_prescription_review_resolved merge correctly.
      const enhancedDrugInteractionResponseFromMessage = buildEnhancedDrugInteractionResponse(message);

      // On-demand patient allergies: orchestration attaches allergy_response only on the turn
      // get_allergy_info was called. Pass it through; preserve any prior value otherwise.
      const allergyResponseFromMessage =
        message?.data?.allergy_response && typeof message.data.allergy_response === 'object'
          ? (message.data.allergy_response as ConversationItem['allergyResponse'])
          : undefined;

      if (!hasMedicationData) {
        setConversations((prev) =>
          safeUpdateConversation(
            prev,
            (conv) => {
              const prevDrug = conv.drugInteractionResponse;
              const fromMessage = enhancedDrugInteractionResponseFromMessage;

              let mergedDrugResponse: typeof fromMessage;
              if (!fromMessage && prevDrug) {
                mergedDrugResponse = { ...prevDrug, intent: (intentStr || prevDrug.intent || "").toString().toLowerCase() };
              } else if (fromMessage) {
                mergedDrugResponse = {
                  ...fromMessage,
                  intent: (intentStr || fromMessage.intent || "").toString().toLowerCase(),
                  high_severity_descriptions:
                    (fromMessage.high_severity_descriptions?.length ? fromMessage.high_severity_descriptions : undefined) ?? prevDrug?.high_severity_descriptions,
                  moderate_severity_descriptions:
                    (fromMessage.moderate_severity_descriptions?.length ? fromMessage.moderate_severity_descriptions : undefined) ?? prevDrug?.moderate_severity_descriptions,
                  minor_severity_descriptions:
                    (fromMessage.minor_severity_descriptions?.length ? fromMessage.minor_severity_descriptions : undefined) ?? prevDrug?.minor_severity_descriptions,
                  current_medication_only_counts: fromMessage.current_medication_only_counts ?? prevDrug?.current_medication_only_counts,
                  high_severity_rtf_primary_medication_name:
                    fromMessage.high_severity_rtf_primary_medication_name?.trim() ||
                    prevDrug?.high_severity_rtf_primary_medication_name,
                  high_severity_prescription_review_resolved:
                    typeof message?.data?.high_severity_prescription_review_resolved === "boolean"
                      ? message.data.high_severity_prescription_review_resolved
                      : fromMessage.high_severity_prescription_review_resolved ??
                        prevDrug?.high_severity_prescription_review_resolved,
                };
              } else {
                mergedDrugResponse = fromMessage;
              }

              return {
                ...conv,
                aiResponse: aiResponse || conv.aiResponse || "",
                isLoading: false,
                prescriptions: [],
                structuredMedicationData: [],
                currentState: message.data?.current_state ?? conv.currentState,
                  mfaPhase: mfaPhaseFromMessage ?? conv.mfaPhase,
                drugInteractionResponse: mergedDrugResponse as ConvDrugResponse | undefined,
                allergyResponse: allergyResponseFromMessage ?? conv.allergyResponse,
                ...intentEnvelope,
                lastIntent: intentEnvelope.lastIntent || intentStr,
                uiBlocks: uiBlocksFromResultData(message.data, conv.uiBlocks),
                appTabLink: appTabLinkFromResultData(message.data, conv.appTabLink),
                appTabLinks: appTabLinksFromResultData(message.data, conv.appTabLinks),
              };
            },
            () => isMountedRef.current,
            "✅ WebSocket: No medication data, merged drug interaction response"
          )
        );
        setIsSearching(false);
        return;
      }

      const medicationDataArray = message.data.medication_data;

      // Process valid medication data (allows dosage/route_of_administration to be empty when READY_TO_CONFIRM)
      const validMedicationData = medicationDataArray.filter(isValidMedicationItem);

      if (validMedicationData.length > 0) {
        // Get current conversation to preserve existing prescriptions' substitutions and pharmacy
        setConversations((prev) => {
          const targetConversation = findTargetConversation(prev);
          if (!targetConversation) return prev;

          // Use pendingSubstitutions if available (from modification request), otherwise preserve existing
          const substitutionsToUse = targetConversation.pendingSubstitutions;

          // Preserve existing prescriptions' substitutions and pharmacy when creating new ones
          const prescriptionOptions: any = {
            aiResponse,
            defaultPharmacy: preferredPharmacy,
            existingPrescriptions: targetConversation.prescriptions || [], // This preserves substitutions if no pending
          };

          // Only add substitutions if pendingSubstitutions is defined (from modification request)
          if (substitutionsToUse !== undefined) {
            prescriptionOptions.substitutions = substitutionsToUse;
          }

          const prescriptions = createPrescriptionsFromMedicationData(validMedicationData, prescriptionOptions);
          const effectiveResponse = aiResponse || targetConversation.aiResponse || "";

          // A turn's primary intent (e.g. "pharmacy_confirmed") can still carry a secondary
          // "suggestions" intent (compound turns like "...and use brooklyn pharmacy" while a
          // strength pick is still pending). Always attempt to build suggestion chips from the
          // payload here too — not just in the dedicated SUGGESTIONS/PHARMACY_* branches above —
          // so those chips aren't silently dropped when they arrive alongside another intent.
          const compoundSuggestionGroups = medicationSuggestionGroupsFromPayload(
            message.data,
            validMedicationData
          );
          const compoundFlatSuggestions = flatLabelsFromSuggestionGroups(compoundSuggestionGroups);

          return safeUpdateConversation(prev, {
            aiResponse: effectiveResponse,
            isLoading: !effectiveResponse || effectiveResponse.trim() === "",
            prescriptions: [...prescriptions],
            originalPrescription: prescriptions[0],
            structuredMedicationData: [...validMedicationData],
            pendingSubstitutions: undefined, // Clear after use
            currentState: message.data?.current_state ?? targetConversation.currentState,
            mfaPhase: mfaPhaseFromMessage ?? targetConversation.mfaPhase,
            drugInteractionResponse: (
              mergeDrugInteractionFromMessage(message, targetConversation.drugInteractionResponse) ??
              targetConversation.drugInteractionResponse
            ) as ConvDrugResponse | undefined,
            allergyResponse: allergyResponseFromMessage ?? targetConversation.allergyResponse,
            ...intentEnvelope,
            lastIntent: intentEnvelope.lastIntent || intentStr,
            uiBlocks: uiBlocksFromResultData(message.data, targetConversation.uiBlocks),
            appTabLink: appTabLinkFromResultData(message.data, targetConversation.appTabLink),
            appTabLinks: appTabLinksFromResultData(message.data, targetConversation.appTabLinks),
            suggestionGroups:
              compoundSuggestionGroups.length > 0
                ? compoundSuggestionGroups
                : targetConversation.suggestionGroups,
            suggestions:
              compoundFlatSuggestions.length > 0
                ? compoundFlatSuggestions
                : message.data?.suggestions || targetConversation.suggestions,
          }, () => isMountedRef.current);
        });
      } else {
        setConversations((prev) =>
          safeUpdateConversation(prev, (conv) => ({
            ...conv,
            aiResponse: aiResponse || conv.aiResponse || "",
            isLoading: !(aiResponse || conv.aiResponse) || (aiResponse || conv.aiResponse || "").trim() === "",
            prescriptions: [],
            structuredMedicationData: [],
            currentState: message.data?.current_state ?? conv.currentState,
            mfaPhase: mfaPhaseFromMessage ?? conv.mfaPhase,
            drugInteractionResponse: (
              mergeDrugInteractionFromMessage(message, conv.drugInteractionResponse) ?? conv.drugInteractionResponse
            ) as ConvDrugResponse | undefined,
            allergyResponse: allergyResponseFromMessage ?? conv.allergyResponse,
            ...intentEnvelope,
            lastIntent: intentEnvelope.lastIntent || intentStr,
            uiBlocks: uiBlocksFromResultData(message.data, conv.uiBlocks),
            appTabLink: appTabLinkFromResultData(message.data, conv.appTabLink),
            appTabLinks: appTabLinksFromResultData(message.data, conv.appTabLinks),
          }), () => isMountedRef.current)
        );
      }

      setIsSearching(false);
    };

    wsManager.setIntermediateMessageCallback((message: any) => {
      if (!isMountedRef.current) return;

      // Handle error type - show error message instead of loader
      if (message.type === WS_MESSAGE_TYPES.ERROR) {
        const errorText =
          message.message ||
          message.data?.message ||
          message.data?.error ||
          "An error occurred";
        setConversations((prev) => {
          if (!isMountedRef.current) return prev;
          const targetConversation = findTargetConversation(prev);
          if (!targetConversation) return prev;
          return updateConversationByIdUtil(
            prev,
            targetConversation.id,
            {
              isLoading: false,
              statusMessages: undefined,
              aiResponse: errorText,
              structuredMedicationData: getPreviousMedicationData(prev.filter((c: any) => c.id !== targetConversation.id)),
            },
            "✅ WebSocket: Error message displayed"
          );
        });
        setIsSearching(false);
        return;
      }

      // Handle pharmacy type
      if (message.type === WS_MESSAGE_TYPES.PHARMACY && message.data) {
        updatePharmacy(message.data);
        return;
      }

      // Live progress from orchestration (e.g. tool_executing / tool_complete).
      // Keep isLoading true until type=result; append each new status line to the checklist.
      if (message.type === WS_MESSAGE_TYPES.STATUS) {
        const statusText =
          (typeof message.data?.message === "string" && message.data.message.trim()) ||
          (typeof message.message === "string" && message.message.trim()) ||
          "";
        if (!statusText) return;

        setConversations((prev) => {
          if (!isMountedRef.current) return prev;
          const targetConversation = findTargetConversation(prev);
          if (!targetConversation) return prev;
          const prevMessages = targetConversation.statusMessages || [];
          // Skip consecutive duplicates so the same line doesn't stack.
          if (prevMessages[prevMessages.length - 1] === statusText) {
            return prev;
          }
          return updateConversationByIdUtil(
            prev,
            targetConversation.id,
            {
              isLoading: true,
              statusMessages: [...prevMessages, statusText],
            },
            "✅ WebSocket: Status update"
          );
        });
        return;
      }

      // Handle token type - skip, keep loading
      if (message.type === WS_MESSAGE_TYPES.TOKEN && message.data) {
        return;
      }

      // Handle result type
      if (message.type === WS_MESSAGE_TYPES.RESULT && message.data) {
        handleResultMessage(message);
      }
    });

    return () => {
      // Deliberately do NOT null out isMountedRef or the wsManager callbacks here.
      // conversations/showResults now live in a module-level store (see
      // conversationUtils.ts) that setConversations/setShowResults write into
      // directly, not component-local React state — so an in-flight agent turn
      // must keep being processed by these callbacks even after this page
      // unmounts (user switched to Medication History, Favourite Drugs, etc.).
      // Nulling the callback here would silently drop that turn's response.
      // The next mount's effect re-registers a fresh (equivalent) callback anyway.
      resultMessageProcessedRef.current = false;

      try {
        localStorage.removeItem("preferredPharmacy");
        localStorage.removeItem("pharmacyIdFromAPI");
      } catch (error) {
        console.error("❌ Error clearing pharmacy data:", error);
      }
    };
  }, [preferredPharmacy, isMountedRef, resultMessageProcessedRef, setConversations, setIsSearching, updatePharmacy]);
};

